import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Typography,
  Box,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  ListItemText,
  InputAdornment
} from '@mui/material';
import StyledFlatpickr from 'components/StyledFlatpickr';

import { format } from 'date-fns';
import { 
  TIME_ZONES, 
  SCHEDULE_FREQUENCIES, 
  DAYS_OF_WEEK,
  cronFromUserInputs
} from '../JobsList/dataSourceConstants';



export const ScheduleSection = ({
  formik,
  loading,
  hasNestedError,
  getNestedErrorMessage,
  scheduleModes,
  timeZoneStrategies
}) => {
  // Local state for user-friendly schedule building
  const [scheduleState, setScheduleState] = useState({
    frequency: 'daily',
    selectedDays: [],
    hour: 0,
    minute: 0
  });

  // Initialize schedule state from cron expression if available
  useEffect(() => {
    if (formik.values.scheduleConfig.mode === 'RECURRING' && formik.values.scheduleConfig.cronExpression) {
      try {
        const cronParts = formik.values.scheduleConfig.cronExpression.split(' ');
        const minute = parseInt(cronParts[1], 10);
        const hour = parseInt(cronParts[2], 10);
        
        // Determine frequency and days based on cron pattern
        let frequency = 'daily';
        let selectedDays = [];
        
        if (cronParts[3] === '*') {
          // Daily schedule
          frequency = 'daily';
        } else if (cronParts[3] === '?' && cronParts[5] !== '*') {
          // Weekly schedule
          frequency = 'weekly';
          selectedDays = cronParts[5].split(',');
        } else if (cronParts[3] !== '*' && cronParts[3] !== '?' && cronParts[4] === '*') {
          // Monthly schedule
          frequency = 'monthly';
          selectedDays = [cronParts[3]];
        }
        
        setScheduleState({
          frequency,
          selectedDays,
          hour: isNaN(hour) ? 0 : hour,
          minute: isNaN(minute) ? 0 : minute
        });
      } catch (error) {
        console.error('Error parsing cron expression:', error);
      }
    }
  }, [formik.values.scheduleConfig.cronExpression]);

  // Update cron expression when schedule inputs change
  useEffect(() => {
    if (formik.values.scheduleConfig.mode === 'RECURRING') {
      const cronExpression = cronFromUserInputs(
        scheduleState.frequency,
        scheduleState.selectedDays,
        scheduleState.hour,
        scheduleState.minute
      );
      formik.setFieldValue('scheduleConfig.cronExpression', cronExpression);
    }
  }, [scheduleState]);

  // Handler for schedule frequency change
  const handleFrequencyChange = (event) => {
    const newFrequency = event.target.value;
    let newSelectedDays = scheduleState.selectedDays;
    
    // Reset days selection for different frequencies
    if (newFrequency === 'daily') {
      newSelectedDays = [];
    } else if (newFrequency === 'weekly' && scheduleState.frequency !== 'weekly') {
      newSelectedDays = ['MON']; // Default to Monday
    } else if (newFrequency === 'monthly' && scheduleState.frequency !== 'monthly') {
      newSelectedDays = ['1']; // Default to 1st day of month
    }
    
    setScheduleState({
      ...scheduleState,
      frequency: newFrequency,
      selectedDays: newSelectedDays
    });
  };

  // Handler for day selection change
  const handleDaysChange = (event) => {
    const selectedDays = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    
    setScheduleState({
      ...scheduleState,
      selectedDays
    });
  };

  // Handler for time change with flatpickr
  const handleTimeChange = (selectedDates) => {
    if (selectedDates && selectedDates[0]) {
      const date = selectedDates[0];
      setScheduleState({
        ...scheduleState,
        hour: date.getHours(),
        minute: date.getMinutes()
      });
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Schedule Mode */}
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">How should this job run?</FormLabel>
          <RadioGroup
            name="scheduleConfig.mode"
            value={formik.values.scheduleConfig.mode}
            onChange={(e) => {
              formik.setFieldValue('scheduleConfig.mode', e.target.value);
            }}
          >
            <FormControlLabel 
              value="RECURRING" 
              control={<Radio disabled={loading} />} 
              label="Run on a schedule" 
              disabled={loading}
            />
            <FormControlLabel 
              value="ONE_TIME" 
              control={<Radio disabled={loading} />} 
              label="Run once at a specific time" 
              disabled={loading}
            />
          </RadioGroup>
          {hasNestedError(formik, 'scheduleConfig', 'mode') && (
            <FormHelperText error>
              {getNestedErrorMessage(formik, 'scheduleConfig', 'mode')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Conditional fields based on schedule mode */}
      {formik.values.scheduleConfig.mode === 'RECURRING' && (
        <RecurringScheduleConfig
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
          scheduleState={scheduleState}
          handleFrequencyChange={handleFrequencyChange}
          handleDaysChange={handleDaysChange}
          handleTimeChange={handleTimeChange}
        />
      )}

      {formik.values.scheduleConfig.mode === 'ONE_TIME' && (
        <OneTimeScheduleConfig
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
        />
      )}

      {/* Time Zone Configuration for both modes */}
      {formik.values.scheduleConfig.mode !== 'ON_DEMAND' && (
        <TimeZoneConfig
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
          timeZoneStrategies={timeZoneStrategies}
        />
      )}

      {/* Optional Schedule Window for RECURRING mode */}
      {formik.values.scheduleConfig.mode === 'RECURRING' && (
        <ScheduleWindowConfig
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
        />
      )}
    </Grid>
  );
};

/**
 * User-friendly recurring schedule configuration component
 */
const RecurringScheduleConfig = ({
  formik,
  loading,
  hasNestedError,
  getNestedErrorMessage,
  scheduleState,
  handleFrequencyChange,
  handleDaysChange,
  handleTimeChange
}) => {
  // Create date object for time picker
  const timeDate = new Date();
  timeDate.setHours(scheduleState.hour || 0);
  timeDate.setMinutes(scheduleState.minute || 0);
  timeDate.setSeconds(0);

  return (
    <>
      <Grid item xs={12} sx={{ pl: 4 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={scheduleState.frequency}
            onChange={handleFrequencyChange}
            label="Frequency"
            disabled={loading}
          >
            {SCHEDULE_FREQUENCIES.map(freq => (
              <MenuItem key={freq.value} value={freq.value}>
                {freq.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>How often this job should run</FormHelperText>
        </FormControl>

        {scheduleState.frequency === 'weekly' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Days of Week</InputLabel>
            <Select
              multiple
              value={scheduleState.selectedDays}
              onChange={handleDaysChange}
              renderValue={(selected) => selected.map(day => 
                DAYS_OF_WEEK.find(d => d.value === day)?.label
              ).join(', ')}
              label="Days of Week"
              disabled={loading}
            >
              {DAYS_OF_WEEK.map(day => (
                <MenuItem key={day.value} value={day.value}>
                  <Checkbox checked={scheduleState.selectedDays.indexOf(day.value) > -1} />
                  <ListItemText primary={day.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select which days of the week to run this job</FormHelperText>
          </FormControl>
        )}

        {scheduleState.frequency === 'monthly' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Day of Month</InputLabel>
            <Select
              value={scheduleState.selectedDays[0] || '1'}
              onChange={(e) => handleDaysChange({ target: { value: [e.target.value] } })}
              label="Day of Month"
              disabled={loading}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <MenuItem key={day} value={day.toString()}>
                  Day {day}
                </MenuItem>
              ))}
              <MenuItem value="L">Last day of month</MenuItem>
            </Select>
            <FormHelperText>Select which day of the month to run this job</FormHelperText>
          </FormControl>
        )}

        <Box sx={{ mb: 2 }}>
          <StyledFlatpickr 
            label="Time"
            value={timeDate}
            onChange={handleTimeChange}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true
            }}
            disabled={loading}
            helperText="At what time of day should this job run"
          />
        </Box>

        <TextField
          fullWidth
          label="Generated Cron Expression"
          value={formik.values.scheduleConfig.cronExpression || ''}
          disabled
          helperText="This expression represents your schedule (technical format)"
          sx={{ mb: 2 }}
        />
      </Grid>
    </>
  );
};

/**
 * One-time schedule configuration component using flatpickr
 */
const OneTimeScheduleConfig = ({
  formik,
  loading,
  hasNestedError,
  getNestedErrorMessage
}) => {
  return (
    <Grid item xs={12} sx={{ pl: 4 }}>
      <StyledFlatpickr
        label="When should this job run?"
        value={formik.values.scheduleConfig.scheduledTime ? new Date(formik.values.scheduleConfig.scheduledTime) : new Date()}
        onChange={(selectedDates) => {
          if (selectedDates && selectedDates[0]) {
            formik.setFieldValue('scheduleConfig.scheduledTime', selectedDates[0].toISOString());
          }
        }}
        options={{
          enableTime: true,
          dateFormat: "Y-m-d H:i",
          time_24hr: true,
          minDate: "today"
        }}
        disabled={loading}
        error={hasNestedError(formik, 'scheduleConfig', 'scheduledTime')}
        helperText={
          hasNestedError(formik, 'scheduleConfig', 'scheduledTime')
            ? getNestedErrorMessage(formik, 'scheduleConfig', 'scheduledTime')
            : "Select the date and time to run this job"
        }
      />
    </Grid>
  );
};

/**
 * Time zone configuration component
 */
const TimeZoneConfig = ({
  formik,
  loading,
  hasNestedError,
  getNestedErrorMessage,
  timeZoneStrategies
}) => {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Time Zone Settings
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            name="scheduleConfig.timeZoneStrategy"
            value={formik.values.scheduleConfig.timeZoneStrategy}
            onChange={(e) => {
              formik.setFieldValue('scheduleConfig.timeZoneStrategy', e.target.value);
            }}
          >
            <FormControlLabel 
              value="ENTITY_LOCAL" 
              control={<Radio disabled={loading} />} 
              label="Store Local Time (use each store's local time zone)" 
              disabled={loading}
            />
            <FormControlLabel 
              value="SPECIFIC" 
              control={<Radio disabled={loading} />} 
              label="Specific Time Zone" 
              disabled={loading}
            />
          </RadioGroup>
          {hasNestedError(formik, 'scheduleConfig', 'timeZoneStrategy') && (
            <FormHelperText error>
              {getNestedErrorMessage(formik, 'scheduleConfig', 'timeZoneStrategy')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {formik.values.scheduleConfig.timeZoneStrategy === 'SPECIFIC' && (
        <Grid item xs={12} sx={{ pl: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Time Zone</InputLabel>
            <Select
              name="scheduleConfig.specificTimeZone"
              value={formik.values.scheduleConfig.specificTimeZone || ''}
              onChange={formik.handleChange}
              label="Time Zone"
              disabled={loading}
              error={hasNestedError(formik, 'scheduleConfig', 'specificTimeZone')}
            >
              {TIME_ZONES.map(tz => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
            {hasNestedError(formik, 'scheduleConfig', 'specificTimeZone') ? (
              <FormHelperText error>
                {getNestedErrorMessage(formik, 'scheduleConfig', 'specificTimeZone')}
              </FormHelperText>
            ) : (
              <FormHelperText>
                Select the time zone to use for scheduling
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      )}
    </>
  );
};

/**
 * Optional schedule window configuration with flatpickr
 */
const ScheduleWindowConfig = ({ formik, loading, hasNestedError, getNestedErrorMessage }) => {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Schedule Window (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can optionally specify when this schedule should begin and end
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <StyledFlatpickr
          label="Start Date (Optional)"
          value={formik.values.scheduleConfig.scheduleStartDate ? new Date(formik.values.scheduleConfig.scheduleStartDate) : null}
          onChange={(selectedDates) => {
            formik.setFieldValue('scheduleConfig.scheduleStartDate', 
              selectedDates && selectedDates[0] ? selectedDates[0].toISOString() : null);
          }}
          options={{
            enableTime: true,
            dateFormat: "Y-m-d H:i",
            time_24hr: true,
            allowInput: true
          }}
          disabled={loading}
          helperText="When this schedule should become active"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <StyledFlatpickr
          label="End Date (Optional)"
          value={formik.values.scheduleConfig.scheduleEndDate ? new Date(formik.values.scheduleConfig.scheduleEndDate) : null}
          onChange={(selectedDates) => {
            formik.setFieldValue('scheduleConfig.scheduleEndDate', 
              selectedDates && selectedDates[0] ? selectedDates[0].toISOString() : null);
          }}
          options={{
            enableTime: true,
            dateFormat: "Y-m-d H:i",
            time_24hr: true,
            allowInput: true
          }}
          disabled={loading}
          helperText="When this schedule should stop running"
        />
      </Grid>
    </>
  );
};